import { Fragment } from 'react'
import PropTypes from 'prop-types'

import theme from 'theme'
import Flex from 'components/common/Flex'
import InputTextField from 'components/common/InputTextField'
import Button from 'components/common/Button'
import RoleDropdown from 'components/authentication/registration/free-edition/RoleDropdown'
import CheckBox from 'components/common/CheckBox'
import PasswordRules from 'components/authentication/PasswordRules'
import passwordOutcome from 'components/authentication/passwordOutcome'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import SignedOutForm, {
  SignedOutFormContainer
} from 'components/authentication/SignedOutForm'
import Image from 'components/common/Image'
import logoGoogle from 'assets/logos/google.svg'

const baseLabelStyles = {
  fontWeight: theme.fontWeights[0],
  marginBottom: theme.space[1]
}

export default function AccountSetupForm(props) {
  const {
    email,
    firstName,
    lastName,
    password,
    confirmPassword,
    role,
    customRole,
    roleHasChanged,
    companyName,
    teamName,
    disabled,
    provider,
    allowUsStorage,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleAllowUsStorageChange,
    handleRoleChange,
    handleCustomRoleChange,
    handleSignUp,
    handleCompanyNameChange,
    handleTeamNameChange
  } = props

  const isGoogle = provider === 'google'

  return (
    <SignedOutFormContainer>
      <SignedOutForm
        title="Account setup"
        onSubmit={event => {
          event.preventDefault()
          handleSignUp && handleSignUp()
        }}
      >
        <Flex column css={{ color: theme.colors.steel[2] }}>
          <Flex css={{ marginBottom: theme.space[4] }}>
            <Flex column>
              <div
                css={[baseLabelStyles, { textAlign: 'left' }]}
                data-test-account-setup-google-full-name={`${firstName} ${lastName}`}
              >{`${firstName} ${lastName}`}</div>
              <div css={baseLabelStyles} data-test-account-setup-email={email}>
                {email}
              </div>
            </Flex>
            {isGoogle && (
              <Flex centered right>
                <Image
                  data-test-account-setup-google-logo
                  css={{ height: theme.space[6] }}
                  src={logoGoogle}
                />
              </Flex>
            )}
          </Flex>
          {!isGoogle && (
            <Fragment>
              <InputTextField
                data-test-account-setup-password
                disabled={disabled}
                innerRef={ref => ref && !isGoogle && ref.focus()}
                label="Create password"
                name="password"
                noRightRadius={true}
                readOnly={!handlePasswordChange}
                type="password"
                value={password}
                onChange={event =>
                  handlePasswordChange &&
                  handlePasswordChange(event.target.value)
                }
              />
              <InputTextField
                data-test-account-setup-confirm-password
                disabled={disabled}
                label="Confirm password"
                name="confirm-password"
                noRightRadius={true}
                readOnly={!handleConfirmPasswordChange}
                type="password"
                value={confirmPassword}
                onChange={event =>
                  handleConfirmPasswordChange &&
                  handleConfirmPasswordChange(event.target.value)
                }
              />
              <div css={{ fontSize: theme.fontSizes[0] }}>
                <PasswordRules
                  passwordOutcome={passwordOutcome(password, confirmPassword)}
                />
              </div>
            </Fragment>
          )}
          <RoleDropdown
            customRole={customRole}
            handleCustomRoleChange={handleCustomRoleChange}
            handleRoleChange={handleRoleChange}
            role={role}
            roleHasChanged={roleHasChanged}
          />
          <Flex>
            <InputTextField
              data-test-account-setup-company-name
              disabled={disabled}
              innerRef={ref => ref && isGoogle && ref.focus()}
              label="Company Name"
              maxLength={50}
              name="company-name"
              noRightRadius={true}
              readOnly={!handleCompanyNameChange}
              value={companyName}
              onChange={event =>
                handleCompanyNameChange &&
                handleCompanyNameChange(event.target.value)
              }
            />
            <InputTextField
              data-test-account-setup-team-name
              disabled={disabled}
              label="Team Name"
              maxLength={50}
              name="team-name"
              noLeftRadius={true}
              readOnly={!handleTeamNameChange}
              value={teamName}
              onChange={event =>
                handleTeamNameChange && handleTeamNameChange(event.target.value)
              }
            />
          </Flex>
          <label
            data-test-allow-us-storage
            css={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: theme.space[4],
              cursor: 'pointer'
            }}
            name="allowUsStorage"
          >
            <CheckBox
              checked={allowUsStorage}
              name="allowUsStorage"
              onChange={() => handleAllowUsStorageChange(!allowUsStorage)}
            />
            <span
              css={{
                marginLeft: theme.space[3],
                fontSize: theme.fontSizes[1]
              }}
            >
              I have read and agree to the{' '}
              <TrackedExternalLink href="https://www.gremlin.com/privacy/">
                privacy policy
              </TrackedExternalLink>
            </span>
          </label>
          <Button
            data-test-account-setup-continue
            stretch
            disabled={
              isFormInvalid({
                isGoogle,
                passwordOutcome: passwordOutcome(password, confirmPassword),
                ...props
              }) || disabled
            }
            label="Go to Dashboard"
            type="submit"
          />
        </Flex>
      </SignedOutForm>
    </SignedOutFormContainer>
  )
}

function isFormInvalid({
  isGoogle,
  passwordOutcome,
  role,
  companyName,
  teamName,
  allowUsStorage
}) {
  const passwordInvalid =
    !isGoogle && passwordOutcome.some(rule => rule.invalid)

  return (
    passwordInvalid ||
    !allowUsStorage ||
    !role ||
    !companyName.length ||
    !teamName.length
  )
}

AccountSetupForm.propTypes = {
  email: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
  confirmPassword: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
  customRole: PropTypes.string.isRequired,
  roleHasChanged: PropTypes.bool.isRequired,
  allowUsStorage: PropTypes.bool.isRequired,
  companyName: PropTypes.string.isRequired,
  provider: PropTypes.string,
  teamName: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  handlePasswordChange: PropTypes.func,
  handleConfirmPasswordChange: PropTypes.func,
  handleRoleChange: PropTypes.func,
  handleCustomRoleChange: PropTypes.func,
  handleAllowUsStorageChange: PropTypes.func,
  handleCompanyNameChange: PropTypes.func,
  handleTeamNameChange: PropTypes.func,
  handleSignUp: PropTypes.func
}

AccountSetupForm.defaultProps = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  confirmPassword: '',
  allowUsStorage: false,
  customRole: '',
  role: '',
  roleHasChanged: false,
  companyName: '',
  provider: '',
  teamName: '',
  disabled: false
}
